import { createI18n } from 'vue-i18n'
// Pas 1.1: only the default locale (ro) ships in the initial bundle.
// The other locales (~630 KB of JSON) load on demand via dynamic
// import() when the user actually switches language (or returns with
// a persisted non-ro locale). Keep this list in sync with
// UI/locales/*.json.
import ro from '../locales/ro.json';

export const SUPPORTED_LOCALES = ['ro', 'en', 'de', 'it', 'cn', 'ar'];

const localeLoaders = {
  en: () => import('../locales/en.json'),
  de: () => import('../locales/de.json'),
  it: () => import('../locales/it.json'),
  cn: () => import('../locales/cn.json'),
  ar: () => import('../locales/ar.json'),
};

const loadedLocales = new Set(['ro']);

export function normalizeLocale(locale) {
  return SUPPORTED_LOCALES.includes(locale) ? locale : 'ro';
}

// Empty message shells keep $i18n.availableLocales listing every
// supported language in the pickers before its chunk is fetched.
const emptyShells = Object.fromEntries(
  SUPPORTED_LOCALES.filter((l) => l !== 'ro').map((l) => [l, {}]),
);

 const i18n = createI18n({
    legacy: false, // you must set `false`, to use Composition API
    locale: 'ro', // Default locale
    // ro is the only locale guaranteed present at first paint, so it
    // is the fallback until another locale chunk has loaded.
    fallbackLocale: 'ro',
    messages: { ro, ...emptyShells },
  });

export async function loadLocaleMessage(locale) {
  const target = normalizeLocale(locale);
  if (loadedLocales.has(target)) return target;
  const loader = localeLoaders[target];
  if (!loader) return target;
  const mod = await loader();
  i18n.global.setLocaleMessage(target, mod.default ?? mod);
  loadedLocales.add(target);
  return target;
}

// NOTE: ar.json is RTL — when locale is 'ar', set document.documentElement.dir = 'rtl' and lang='ar'.
// Example: document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'; document.documentElement.lang = locale;
// This ensures proper text direction for Arabic.
// Returns a promise for non-ro locales (chunk fetch); for ro (already
// bundled) the switch is applied synchronously, as before.
function setupLocale(locale) {
  const target = normalizeLocale(locale);
  const apply = () => {
    i18n.global.locale.value = target;
    if (typeof document !== 'undefined') {
      document.documentElement.lang = target;
      document.documentElement.dir = target === 'ar' ? 'rtl' : 'ltr';
    }
  };
  if (loadedLocales.has(target)) {
    apply();
    return Promise.resolve(target);
  }
  return loadLocaleMessage(target).then(apply).then(() => target);
}

function getTranslation(key, data = {}) {
  if (i18n) {
    const translation = i18n.global.t(key, data);
    if (translation !== key) {
      return translation;
    }
  }

  // Replace placeholders in the original key with the corresponding values from the data
  return key.replace(/\{(\w+)\}/g, (match, placeholder) => {
    return data[placeholder] || `{${placeholder}}`;
  });
}

export { i18n, getTranslation, setupLocale};
